import React, { useEffect, useMemo, useRef, useState } from "react";

import { css } from "@compiled/react";
import {
    ComponentProps,
    UniformSlot,
    UniformSlotWrapperComponentProps,
    useUniformCurrentComposition
} from "@uniformdev/canvas-react";
import { useIntl } from "react-intl";
import { AGGClient } from "src/clients/agg-client/agg-client.ts";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import { ProductKey } from "@/components/signup/utils/products";
import { wacToken } from "@/libraries/design/foundations";
import { setGrsDataToLocalStorage } from "@/utils/anchor-queries/get-bandits-contents";
import { useExperiment } from "@/utils/experiment/use-experiment.ts";
import { isProduction } from "@/utils/urls";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client.tsx";
import {
    UserSegmentTypes,
    useUserSegments
} from "../../personalization-container/src/use-user-segments";

import type { ComponentInstance } from "@uniformdev/canvas";

const WRAPPER_STYLES = css({
    position: "relative"
});

const PANEL_STYLES = css({
    position: "absolute",
    insetBlockStart: token("space.050"),
    insetInlineEnd: token("space.050"),
    zIndex: 2147483001,
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "stretch",
    color: token("color.text.inverse"),
    border: 0,
    borderRadius: "8px",
    maxWidth: "260px",
    padding: `${token("space.050")} ${token("space.075")}`,
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(4px)",
    opacity: 0.76,
    transition:
        "background-color 220ms ease, backdrop-filter 220ms ease, opacity 220ms ease, padding 220ms ease"
});

const PANEL_EXPANDED_STYLES = css({
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(12px)",
    opacity: 1,
    padding: token("space.075")
});

// Inner row that keeps the panel name and the variant <select> side-by-side
// while the experiment-override toggle stacks beneath them.
const PANEL_ROW_STYLES = css({
    display: "inline-flex",
    alignItems: "center",
    columnGap: token("space.050"),
    minWidth: 0
});

const LABEL_STYLES = css({
    fontSize: "11px",
    lineHeight: 1.2,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    maxWidth: "140px"
});

const SELECT_STYLES = css({
    "border": 0,
    "borderRadius": "999px",
    "padding": `${token("space.025")} ${token("space.075")}`,
    "fontSize": "10px",
    "lineHeight": 1.2,
    "fontWeight": 700,
    "cursor": "pointer",
    "color": token("color.text"),
    "backgroundColor": token("elevation.surface"),
    ":focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const EXPERIMENT_TOGGLE_ROW_STYLES = css({
    overflow: "hidden",
    maxHeight: 0,
    marginBlockStart: 0,
    opacity: 0,
    pointerEvents: "none",
    transform: "translateY(-4px)",
    transition:
        "max-height 220ms ease, margin-block-start 220ms ease, opacity 220ms ease, transform 220ms ease"
});

const EXPERIMENT_TOGGLE_ROW_EXPANDED_STYLES = css({
    maxHeight: "32px",
    marginBlockStart: token("space.100"),
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateY(0)"
});

const EXPERIMENT_TOGGLE_LABEL_STYLES = css({
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    justifyContent: "space-between",
    columnGap: token("space.100"),
    fontSize: "11px",
    lineHeight: 1.2,
    fontWeight: 700,
    cursor: "pointer",
    userSelect: "none"
});

// Small native checkbox sized to fit alongside the existing variant <select>.
const EXPERIMENT_TOGGLE_CHECKBOX_STYLES = css({
    "width": "12px",
    "height": "12px",
    "margin": 0,
    "cursor": "pointer",
    ":disabled": {
        opacity: 0.4,
        cursor: "not-allowed"
    },
    ":focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

export interface UniformContextualBanditsContainerProps extends ComponentProps {
    name: string;
    useCaseId: string;
    experimentId?: string;
}

const OVERRIDE_LOADING = "loading" as const;
const OVERRIDE_DEFAULT = "default" as const;
type OverrideValue = typeof OVERRIDE_LOADING | typeof OVERRIDE_DEFAULT | number;

const getBootstrapAnonymousId = (): string =>
    (typeof window !== "undefined" &&
        window.featureGateBootstrapValues?.identifiers?.analyticsAnonymousId) ||
    "";

/**
 * Identity wrapper for `<UniformSlot>`. Renders whatever `items` array it is
 * given, in order. Used as the base wrapper when delegating selection to a
 * higher-level wrapper, and on the `default` slot.
 */
const VariantSelector = (props: UniformSlotWrapperComponentProps) => (
    <React.Fragment>
        {props.items.map((item, index) => (
            <React.Fragment key={`variant-${index}`}>{item}</React.Fragment>
        ))}
    </React.Fragment>
);

/**
 * Renders the `variants` slot but slices its `items` array down to a single
 * entry by index, so only the chosen variant is mounted.
 */
const renderChosenVariant = (chosenVariantIndex: number) => (
    <UniformSlot
        name="variants"
        wrapperComponent={(slotProps) =>
            VariantSelector({
                ...slotProps,
                items: [slotProps.items[chosenVariantIndex]]
            })
        }
    />
);

const useIsEnrolledInExperiment = ({
    experimentId = ""
}: {
    experimentId?: string;
}) => {
    const hasExperimentId = experimentId.length > 0;

    const experimentStatus = useExperiment({
        experimentName: experimentId,
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            // Do not fire exposure when there is no experiment to expose to.
            fireExperimentExposure: hasExperimentId,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            // Skip targeting evaluation entirely when no experiment is set.
            cohort: () => hasExperimentId
        },
        metadata: {
            description:
                "Contextual Bandits Experiment - configured in Uniform",
            expectedReleaseDate: "unknown - configured in Uniform",
            expectedRuntime: "unknown - configured in Uniform",
            context: {
                urls: []
            }
        }
    });

    return (
        hasExperimentId &&
        experimentStatus.type === "enrolled" &&
        experimentStatus.value
    );
};

const toggleExperimentOverride = (
    setExperimentOverride: React.Dispatch<React.SetStateAction<boolean>>
): void => {
    setExperimentOverride((prev) => !prev);
};

export interface WacAnchorJourneyJsmGrsParams {
    locale?: string;
    anonymousId?: string;
    subAnchor?: string;
    channel?: string;
    utmContent?: string;
}

export interface WacAnchorJourneyJsmGrsResponse {
    growthRecommendations?: {
        recommendations?: {
            data?: Array<{
                entityId: string;
                reasons: string[];
            }>;
            context?: {
                sessionId: string;
            };
            identifier?: string;
            message?: string;
        };
    };
}

export const fetchWacAnchorJourneyJsmRecommendations = async ({
    locale = "en-US",
    anonymousId,
    subAnchor = "",
    channel = "",
    utmContent = ""
}: WacAnchorJourneyJsmGrsParams = {}): Promise<WacAnchorJourneyJsmGrsResponse | null> => {
    const aggClient = new AGGClient({
        gatewayUrl: isProduction()
            ? "https://www.atlassian.com/gateway/api/graphql"
            : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
    });

    const query = `
query WacAnchorJourneyJsm {
  growthRecommendations {
    recommendations(
      context: {
        tenantId: "",
        product: "jira",
        subproduct: "servicemanagement",
        containers: null,
        sessionId: "",
        locale: "${locale}",
        orgId: "",
        workspaceId: null,
        useCase: "wacAnchorJourneyJsm",
        anonymousId: "${anonymousId}",
        custom: {
          subAnchor: "${subAnchor}",
          channel: "${channel}",
          utmContent: "${utmContent}"
        }
      },
      first: 1,
      rerank: []
    ) {
      ... on GrowthRecRecommendations {
        data {
          entityId,
          reasons
        },
        context {
          sessionId
        }
      }
      ... on QueryError {
        identifier
        message
      }
    }
  }
}
`;

    const result = await aggClient.query(query, {});
    return result.data as WacAnchorJourneyJsmGrsResponse;
};

export const parseWacAnchorJourneyJsmRecommendations = (
    data: WacAnchorJourneyJsmGrsResponse | null
): string[] => {
    const recommendations = data?.growthRecommendations?.recommendations;

    if (!recommendations) {
        console.error(
            "No recommendations found in wacAnchorJourneyJsm GRS response."
        );
        return [];
    }

    if (Array.isArray(recommendations.data)) {
        return recommendations.data.map(({ entityId }) => entityId);
    }

    console.error(
        "Unknown response format for wacAnchorJourneyJsm GRS recommendations",
        recommendations
    );
    return [];
};

export const parseWacAnchorJourneyJsmRecoSessionId = (
    data: WacAnchorJourneyJsmGrsResponse | null
): string | null =>
    data?.growthRecommendations?.recommendations?.context?.sessionId ?? null;

const getVariantId = (variant: ComponentInstance): string | null => {
    const variantId = variant.parameters?.["id"]?.value;
    return typeof variantId === "string" ? variantId : null;
};

const getBanditChosenIndex = (
    recommendationEntityIds: string[],
    variantSlots: ComponentInstance[]
): number | null => {
    const recommendationEntityId = recommendationEntityIds[0];
    if (!recommendationEntityId) {
        return null;
    }

    const matchingVariantIndex = variantSlots.findIndex(
        (variant) => getVariantId(variant) === recommendationEntityId
    );

    return matchingVariantIndex >= 0 ? matchingVariantIndex : null;
};

const UniformContextualBanditsContainer = (
    props: UniformContextualBanditsContainerProps
    // eslint-disable-next-line complexity
) => {
    const isProd = isProduction();

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const composition = useUniformCurrentComposition();
    const compositionId = composition?.data?._id;

    // Stable reference to the `variants` slot entries. Required so authors can
    // see how many variant candidates exist and so downstream selection logic
    // does not re-run on every render.
    const variantSlots = useMemo(
        () => props.component.slots?.["variants"] ?? [],
        [props.component.slots]
    );
    const variantSlotsRef = useRef(variantSlots);
    useEffect(() => {
        variantSlotsRef.current = variantSlots;
    }, [variantSlots]);

    // Dev-only override. In production this stays at `OVERRIDE_DEFAULT` and the
    // <select> is never rendered, so the value has no effect.
    const [override, setOverride] = useState<OverrideValue>(OVERRIDE_DEFAULT);

    const [isExperimentOverridden, setIsExperimentOverridden] =
        useState<boolean>(false);

    const [isPanelExpanded, setIsPanelExpanded] = useState<boolean>(false);

    const [banditRecommendationEntityIds, setBanditRecommendationEntityIds] =
        useState<string[]>([]);

    // True until the GRS recommendation request settles (client-side only). On
    // the server this stays true so the skeleton is what gets SSR'd, and the
    // client hydrates the same markup before swapping in the resolved variant —
    // avoiding a flash of the (often empty) default slot and layout shift.
    const [isLoadingRecommendation, setIsLoadingRecommendation] =
        useState<boolean>(true);

    const intl = useIntl();
    const locale = intl.locale;
    const anonymousId =
        analyticsClient?.getAnonymousId() ?? getBootstrapAnonymousId();

    const { data: personalizationValues } = useUserSegments();
    const subAnchor = personalizationValues?.[UserSegmentTypes.SUB_ANCHOR];
    const channel = personalizationValues?.[UserSegmentTypes.CHANNEL];
    const utmContent = personalizationValues?.[UserSegmentTypes.UTM_CONTENT];

    const banditChosenIndex = useMemo(
        () => getBanditChosenIndex(banditRecommendationEntityIds, variantSlots),
        [banditRecommendationEntityIds, variantSlots]
    );

    const chosenVariantIndex: number | null =
        !isProd &&
        override !== OVERRIDE_DEFAULT &&
        override !== OVERRIDE_LOADING
            ? override
            : banditChosenIndex;

    const isEnrolledInExperiment = useIsEnrolledInExperiment({
        experimentId: props.experimentId
    });
    const variantChosen =
        chosenVariantIndex !== null && variantSlots[chosenVariantIndex];

    const isEnrolledInExperimentPlusOverride =
        !props.experimentId ||
        isEnrolledInExperiment ||
        (isExperimentOverridden && !isProd);

    const shouldRenderVariant =
        isEnrolledInExperimentPlusOverride && variantChosen;

    const renderedVariant = shouldRenderVariant ? (
        renderChosenVariant(chosenVariantIndex)
    ) : (
        <UniformSlot name="default" wrapperComponent={VariantSelector} />
    );

    // Optional, author-composed skeleton. The container is content-agnostic —
    // it doesn't know what the chosen variant will render — so the loading
    // placeholder is supplied as a Uniform slot rather than hard-coded here.
    // When no `loading` slot is authored this is empty and behaviour is
    // unchanged (the default slot renders during the request).
    const hasLoadingSlot =
        (props.component.slots?.["loading"]?.length ?? 0) > 0;

    // Show the author-composed loading slot when either:
    //  - the GRS request is still in flight in the real flow — this is the only
    //    path that runs in production, where `override` is always
    //    `OVERRIDE_DEFAULT`; or
    //  - a developer explicitly picks "loading" in the non-production override
    //    panel to preview the skeleton (forced, regardless of request state).
    // A specific variant/default override is honoured immediately and is never
    // masked by the in-flight skeleton, so the panel stays usable in the
    // Uniform preview even if the recommendation request never settles there.
    const showLoadingSlot =
        hasLoadingSlot &&
        (override === OVERRIDE_LOADING ||
            (override === OVERRIDE_DEFAULT && isLoadingRecommendation));

    const content = showLoadingSlot ? (
        <UniformSlot name="loading" wrapperComponent={VariantSelector} />
    ) : (
        renderedVariant
    );

    useEffect(() => {
        // Guard against SSR — AGGClient makes a network call that should only
        // happen in the browser, where anonymousId/sessionId/etc. are available.
        if (typeof window === "undefined") {
            return;
        }

        let cancelled = false;

        void (async () => {
            let recommendationEntityIds: string[] = [];

            try {
                const response = await fetchWacAnchorJourneyJsmRecommendations({
                    locale,
                    anonymousId,
                    subAnchor,
                    channel,
                    utmContent
                });

                recommendationEntityIds =
                    parseWacAnchorJourneyJsmRecommendations(response);
                const recoSessionId =
                    parseWacAnchorJourneyJsmRecoSessionId(response);

                if (recoSessionId) {
                    setGrsDataToLocalStorage(
                        {
                            recoSessionId,
                            recoEntityId: recommendationEntityIds[0] ?? ""
                        },
                        ProductKey.JIRA_SERVICE_MANAGEMENT
                    );
                }

                if (!cancelled) {
                    setBanditRecommendationEntityIds(recommendationEntityIds);
                }
            } catch (error) {
                console.error("Failed to fetch GRS recommendations", error);
            } finally {
                if (cancelled) {
                    return;
                }

                // Request settled — stop showing the skeleton and reveal the
                // resolved variant (or default fallback).
                setIsLoadingRecommendation(false);

                const currentVariantSlots = variantSlotsRef.current;
                const resolvedChosenVariantIndex = getBanditChosenIndex(
                    recommendationEntityIds,
                    currentVariantSlots
                );
                const resolvedVariant =
                    resolvedChosenVariantIndex !== null
                        ? currentVariantSlots[resolvedChosenVariantIndex]
                        : undefined;
                const resolvedShouldRenderVariant =
                    isEnrolledInExperimentPlusOverride && resolvedVariant;
                const resolvedSelectedVariantKey =
                    resolvedShouldRenderVariant && resolvedVariant
                        ? getVariantId(resolvedVariant) ??
                          `variant_${resolvedChosenVariantIndex}`
                        : "default";

                if (analyticsClient) {
                    createAnalyticsEvent({
                        name: "waeBanditVariantRender",
                        action: "banditVariantRender",
                        actionSubject: "uniformPage",
                        eventType: "screen",
                        source: window.location.pathname,
                        attributes: {
                            containerName: props.name,
                            path: window.location.pathname,
                            compositionId: compositionId,
                            variantName: resolvedSelectedVariantKey,
                            status: resolvedSelectedVariantKey,
                            recommendations: recommendationEntityIds
                        }
                    }).fire();
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        analyticsClient,
        anonymousId,
        channel,
        compositionId,
        createAnalyticsEvent,
        isEnrolledInExperimentPlusOverride,
        locale,
        props.name,
        subAnchor,
        utmContent
    ]);

    if (isProd) {
        return content;
    }

    return (
        <div css={WRAPPER_STYLES}>
            <div
                css={[PANEL_STYLES, isPanelExpanded && PANEL_EXPANDED_STYLES]}
                onMouseEnter={() => setIsPanelExpanded(true)}
                onMouseLeave={() => setIsPanelExpanded(false)}
                onFocus={() => setIsPanelExpanded(true)}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setIsPanelExpanded(false);
                    }
                }}
            >
                <label css={PANEL_ROW_STYLES}>
                    <span css={LABEL_STYLES}>{props.name}</span>
                    <select
                        css={SELECT_STYLES}
                        aria-label={`Contextual bandits variant override for ${props.useCaseId}`}
                        value={String(override)}
                        onChange={(event) => {
                            const next = event.target.value;
                            setOverride(
                                next === OVERRIDE_LOADING
                                    ? OVERRIDE_LOADING
                                    : next === OVERRIDE_DEFAULT
                                    ? OVERRIDE_DEFAULT
                                    : Number(next)
                            );
                        }}
                    >
                        {hasLoadingSlot && (
                            <option value={OVERRIDE_LOADING}>loading</option>
                        )}
                        <option value={OVERRIDE_DEFAULT}>default</option>
                        {variantSlots.map((_, index) => (
                            <option key={index} value={index}>
                                variant {index}
                            </option>
                        ))}
                    </select>
                </label>
                <div
                    css={[
                        EXPERIMENT_TOGGLE_ROW_STYLES,
                        isPanelExpanded && EXPERIMENT_TOGGLE_ROW_EXPANDED_STYLES
                    ]}
                    aria-hidden={!isPanelExpanded}
                >
                    <label
                        css={EXPERIMENT_TOGGLE_LABEL_STYLES}
                        title={
                            props.experimentId
                                ? `Force experiment "${props.experimentId}" to behave as enrolled in non-production environments. Does not affect real Statsig enrollment.`
                                : "No experimentId is configured on this contextual bandits container, so there is no experiment to override."
                        }
                    >
                        <span>Experiment Override</span>
                        <input
                            type="checkbox"
                            css={EXPERIMENT_TOGGLE_CHECKBOX_STYLES}
                            aria-label={`Force experiment enrollment override for ${props.useCaseId}`}
                            disabled={!props.experimentId}
                            checked={isExperimentOverridden}
                            // Removed from the tab order while the row is
                            // collapsed and visually hidden. Restored when the
                            // panel is hovered / focused.
                            tabIndex={isPanelExpanded ? 0 : -1}
                            onChange={() =>
                                toggleExperimentOverride(
                                    setIsExperimentOverridden
                                )
                            }
                        />
                    </label>
                </div>
            </div>
            {content}
        </div>
    );
};

export default UniformContextualBanditsContainer;
